// src/components/FormField/index.tsx
import { FC, ReactNode } from 'react';
import { Input, Select } from 'antd';
import type { InputProps } from 'antd';
import type { SelectProps } from 'antd';
import SvgIcon from '@/components/SvgIcon';
import styles from './index.module.scss';

interface FormFieldProps {
  label: string;
  required?: boolean;
  placeholder?: string;
  type?: 'input' | 'select';
  options?: SelectProps['options'];
  value?: string;
  onChange?: (value: string) => void;
  suffix?: ReactNode;
  selectProps?: SelectProps;
  optionalHint?: string;
  prefixIcon?: ReactNode; // 选中状态的前缀图标
  uploadOptionValue?: string; // Upload new 选项的 value，用于特殊样式
}

const FormField: FC<FormFieldProps> = ({
  label,
  required = false,
  placeholder = 'Enter...',
  type = 'input',
  options = [],
  value,
  onChange,
  suffix,
  selectProps,
  optionalHint,
  prefixIcon,
  uploadOptionValue,
}) => {
  const handleInputChange: InputProps['onChange'] = (e) => {
    onChange?.(e.target.value);
  };

  const handleSelectChange: SelectProps['onChange'] = (val, option) => {
    onChange?.(val as string);
    selectProps?.onChange?.(val, option);
  };

  return (
    <div className={styles.formField}>
      <div className={styles.label}>
        <span className={styles.labelText}>{label}</span>
        {required && <span className={styles.required}>*</span>}
        {optionalHint && <span className={styles.optionalHint}>{optionalHint}</span>}
      </div>
      {type === 'input' ? (
        <Input className={styles.input} placeholder={placeholder} value={value} onChange={handleInputChange} suffix={suffix} />
      ) : (
        <Select
          {...selectProps}
          className={`${styles.select} ${selectProps?.className || ''}`}
          classNames={{
            ...selectProps?.classNames,
            popup: {
              ...selectProps?.classNames?.popup,
              root: `${styles.selectPopup} ${selectProps?.classNames?.popup?.root || ''}`,
            },
          }}
          placeholder={placeholder}
          options={options}
          value={value || undefined}
          onChange={handleSelectChange}
          labelRender={
            prefixIcon
              ? (label) => (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {prefixIcon}
                    {label.label}
                  </span>
                )
              : undefined
          }
          optionRender={
            uploadOptionValue
              ? (option) => {
                  if (option.value === uploadOptionValue) {
                    return (
                      <div className={styles.uploadOption}>
                        <SvgIcon name="plus-black" size={16} className={styles.uploadIcon} />
                        <span>{option.label}</span>
                      </div>
                    );
                  }
                  const isSelected = option.value === value;
                  return (
                    <div className={`${styles.fileOption} ${isSelected ? styles.fileOptionSelected : ''}`}>
                      <span>{option.label}</span>
                      {isSelected && <SvgIcon name="check1" size={16} className={styles.checkIcon} />}
                    </div>
                  );
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default FormField;
